import React, { useState, useMemo, useEffect } from "react";
import { formatDateReadable } from "../requests/utilities/helpers";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import SearchIcon from "@mui/icons-material/Search";
import StoreIcon from "@mui/icons-material/Store";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { getSalesCompPercent } from "./overviewSalesComparison";
import {
  OverviewTooltip,
  TooltipCard,
  TOOLTIP_WIDTH_CLASS,
  TooltipHeader,
  TooltipDivider,
  TooltipNote,
  TooltipSectionLabel,
  CompareRow,
  CompareHeader,
  MetricTableRow,
  MetricTableHeader,
  DeltaValue,
} from "./OverviewTooltip";

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const waitForOverviewLoadingPaint = () =>
  new Promise((resolve) => {
    if (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
    ) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resolve);
      });
      return;
    }

    setTimeout(resolve, 0);
  });

const formatCurrency = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const formatHourPair = (actual, comparison) =>
  `${toNumber(actual).toFixed(2)} / ${toNumber(comparison).toFixed(2)} hrs`;

const hasFiniteNumber = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  Number.isFinite(Number(value));

const createNullableAggregate = () => ({
  total: null,
  hasMissingValue: false,
});

const addNullableAggregateValue = (aggregate, value) => {
  if (!hasFiniteNumber(value)) {
    aggregate.hasMissingValue = true;
    return;
  }

  aggregate.total =
    (aggregate.total === null ? 0 : aggregate.total) + Number(value);
};

const resolveNullableAggregate = (aggregate, requireComplete) =>
  requireComplete && aggregate.hasMissingValue ? null : aggregate.total;

// Adds one store's side of a comparison, deciding whether a missing figure
// should withhold the whole comparison.
//
// A store with nothing on either side was not trading in this period, in either
// year, so leaving it out keeps both sides balanced and lets the stores that
// were open compare normally. Anything else missing still withholds: a date
// that is still open in that store's own timezone, or a store with last year
// but no figure yet this year, would otherwise put a subset of this year up
// against the whole of last year and read as a decline that has not happened.
const addComparisonValue = (aggregate, value, priorYearValue) => {
  if (!hasFiniteNumber(value) && !hasFiniteNumber(priorYearValue)) return;

  addNullableAggregateValue(aggregate, value);
};

const capitalizeLabel = (label) =>
  label ? `${label.charAt(0).toUpperCase()}${label.slice(1)}` : "";

const formatSplh = (sales, hours) => {
  if (!hasFiniteNumber(sales) || !hasFiniteNumber(hours)) return "-";
  const hourNumber = Number(hours);
  if (hourNumber <= 0) return "-";
  return formatCurrency(Number(sales) / hourNumber);
};

const formatOptionalCurrency = (value) =>
  hasFiniteNumber(value) ? formatCurrency(value) : "-";

// Column widths for the labor tables. Kept next to each other so the two
// variants stay visually consistent.
const LABOR_COST_COLUMNS = ["w-[72px]", "w-[62px]", "w-[56px]"];
const LABOR_SPLH_COLUMNS = ["w-[72px]", "w-[74px]"];

const getLaborPercentNumber = (laborCost, sales) => {
  if (!hasFiniteNumber(laborCost) || !hasFiniteNumber(sales)) return null;
  const salesNumber = Number(sales);
  if (salesNumber <= 0) return null;
  return (Number(laborCost) / salesNumber) * 100;
};

const getSplhNumber = (sales, hours) => {
  if (!hasFiniteNumber(sales) || !hasFiniteNumber(hours)) return null;
  const hourNumber = Number(hours);
  if (hourNumber <= 0) return null;
  return Number(sales) / hourNumber;
};

const getNumericVariance = (value, target) =>
  value === null || target === null ? null : value - target;

const formatLaborPercent = (laborCost, sales) => {
  if (!hasFiniteNumber(laborCost) || !hasFiniteNumber(sales)) return "-";
  const salesNumber = Number(sales);
  if (salesNumber <= 0) return "-";
  return `${((Number(laborCost) / salesNumber) * 100).toFixed(1)}%`;
};

const getLetterGrade = (score) => {
  if (score >= 90) return "A";
  if (score >= 80) return "B";
  if (score >= 70) return "C";
  if (score >= 60) return "D";
  if (score === 0) return "-";
  return "F";
};

function OpsChip({ label, value, tone = "warn" }) {
  const toneClass =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : "border-amber-200 bg-amber-50 text-amber-700";

  const hasValue = value !== undefined && value !== null && value !== "";

  return (
    <span
      className={`whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] font-semibold ${toneClass}`}
    >
      {label}
      {hasValue ? ` ${value}` : ""}
    </span>
  );
}

function GradeBadge({ score }) {
  const grade = getLetterGrade(score);
  const gradeClass =
    grade === "A"
      ? "bg-emerald-600 text-white"
      : grade === "B"
      ? "bg-lime-600 text-white"
      : grade === "C"
      ? "bg-amber-500 text-white"
      : grade === "D"
      ? "bg-orange-500 text-white"
      : grade === "-"
      ? "bg-slate-200 text-slate-600"
      : "bg-rose-500 text-white";

  return (
    <span className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md px-2 text-sm font-bold ${gradeClass}`}>
      {grade}
    </span>
  );
}

function LaborMetricTooltip({
  heading,
  salesLabel = "Sales",
  sales,
  valueLabel,
  valueHours,
  valueLaborCost,
  targetLabel,
  targetHours,
  targetLaborCost,
  showLaborPercent,
}) {
  const valueHoursNumber = toNumber(valueHours);
  const targetHoursNumber = toNumber(targetHours);
  const hoursVariance = valueHoursNumber - targetHoursNumber;
  const costVariance = getNumericVariance(valueLaborCost, targetLaborCost);
  const laborPercentVariance = getNumericVariance(
    getLaborPercentNumber(valueLaborCost, sales),
    getLaborPercentNumber(targetLaborCost, sales)
  );
  const splhVariance = getNumericVariance(
    getSplhNumber(sales, valueHours),
    getSplhNumber(sales, targetHours)
  );
  const salesBasis = `${salesLabel.toLowerCase()} of ${formatOptionalCurrency(sales)}`;

  const columnLabels = showLaborPercent
    ? ["Hours", "Cost", "Labor %"]
    : ["Hours", "SPLH"];
  const columnClasses = showLaborPercent
    ? LABOR_COST_COLUMNS
    : LABOR_SPLH_COLUMNS;

  const rowValues = (hours, cost, percent, splh) =>
    showLaborPercent ? [hours, cost, percent] : [hours, splh];

  return (
    <>
      {heading ? <TooltipSectionLabel>{heading}</TooltipSectionLabel> : null}

      {/* One row per thing being measured, with its attributes as columns.
          The target row is the baseline and the variance row carries the
          deltas for every column rather than hours alone. */}
      <MetricTableHeader labels={columnLabels} colClasses={columnClasses} />

      <MetricTableRow
        label={targetLabel}
        colClasses={columnClasses}
        isBaseline
        values={rowValues(
          `${targetHoursNumber.toFixed(2)} hrs`,
          formatOptionalCurrency(targetLaborCost),
          formatLaborPercent(targetLaborCost, sales),
          formatSplh(sales, targetHours)
        )}
      />

      <MetricTableRow
        label={valueLabel}
        colClasses={columnClasses}
        emphasis
        values={rowValues(
          `${valueHoursNumber.toFixed(2)} hrs`,
          formatOptionalCurrency(valueLaborCost),
          formatLaborPercent(valueLaborCost, sales),
          formatSplh(sales, valueHours)
        )}
      />

      <MetricTableRow
        label="Variance"
        colClasses={columnClasses}
        values={rowValues(
          <DeltaValue
            value={hoursVariance}
            display={`${Math.abs(hoursVariance).toFixed(2)} hrs`}
            invert
          />,
          <DeltaValue
            value={costVariance}
            display={
              costVariance === null
                ? undefined
                : formatCurrency(Math.abs(costVariance))
            }
            invert
          />,
          <DeltaValue
            value={laborPercentVariance}
            display={
              laborPercentVariance === null
                ? undefined
                : `${Math.abs(laborPercentVariance).toFixed(1)} pts`
            }
            invert
          />,
          // More sales per labor hour is the good outcome, so this one is not
          // inverted the way hours, cost and labor % are.
          <DeltaValue
            value={splhVariance}
            display={
              splhVariance === null
                ? undefined
                : formatCurrency(Math.abs(splhVariance))
            }
          />
        )}
      />

      {/* Each section is measured against a different denominator, so the
          basis sits inside the section rather than in one shared footnote. */}
      <p className="mt-1 px-2 text-[11px] leading-relaxed text-slate-400">
        Based on {salesBasis}.
      </p>
    </>
  );
}

// One card covering every labor row on the box. Planned and actual are the
// same story for a finished period, so splitting them across two tooltips
// meant holding the first half in your head while hovering the second.
function LaborTooltip({ sections, showLaborPercent }) {
  const isSingleSection = sections.length === 1;

  return (
    <TooltipCard className={TOOLTIP_WIDTH_CLASS}>
      <TooltipHeader
        title={isSingleSection ? `${sections[0].heading} Labor` : "Labor"}
      />
      {sections.map((section, index) => (
        <React.Fragment key={section.heading}>
          {index > 0 ? <TooltipDivider /> : null}
          <LaborMetricTooltip
            {...section}
            heading={isSingleSection ? null : section.heading}
            showLaborPercent={showLaborPercent}
          />
        </React.Fragment>
      ))}
    </TooltipCard>
  );
}

function LaborProgressSummary({
  label,
  actual,
  target,
  targetLabel,
  score,
  muted = false,
  barClassName = "w-[75%]",
  tooltipTitle = "",
}) {
  const actualNumber = toNumber(actual);
  const targetNumber = toNumber(target);
  const ratio = targetNumber > 0 ? (actualNumber / targetNumber) * 100 : null;
  const ratioIsOver = ratio !== null && ratio > 100;
  const ratioClass =
    ratio === null
      ? "text-slate-500"
      : ratioIsOver
      ? "text-rose-700"
      : "text-emerald-700";
  const cappedRatio = Math.max(0, Math.min(100, ratio || 0));

  const bar = (
    <div className={`${barClassName} cursor-pointer`}>
      <div className="mb-1 flex items-center justify-between gap-2 text-xs font-medium">
        <span className="font-semibold text-slate-700">{label}</span>
        <span className="shrink-0 whitespace-nowrap text-right text-slate-600">
          {formatHourPair(actualNumber, targetNumber)}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-200">
        <div
          className="h-full rounded-full bg-slate-700 transition-all duration-300"
          style={{ width: `${cappedRatio}%` }}
        />
      </div>
      <div className="mt-1">
        <p className={`text-sm font-semibold ${ratioClass}`}>
          {ratio === null ? "0.0" : ratio.toFixed(1)}%{" "}
          <span className="text-xs font-medium text-slate-500">
            of {targetLabel}
          </span>
        </p>
      </div>
    </div>
  );

  return (
    <div
      className={`flex items-end justify-between gap-3 ${
        muted ? "opacity-60" : ""
      }`}
    >
      {/* Omitted when a parent wraps several rows in one shared tooltip. */}
      {tooltipTitle ? (
        <OverviewTooltip title={tooltipTitle}>{bar}</OverviewTooltip>
      ) : (
        bar
      )}
      <div className="shrink-0">
        <GradeBadge score={score} />
      </div>
    </div>
  );
}

const getAdjustmentCount = (source, singularKey, pluralKey) =>
  toNumber(source?.[singularKey] ?? source?.[pluralKey]);

const formatDayLabel = (dateValue) => {
  if (!dateValue) return "-";
  const parsedDate = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return formatDateReadable(dateValue);
  }

  return parsedDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

// The backend aligns prior-year sales on a 52-week offset so the comparison
// lands on the same weekday, not the same calendar date.
const FISCAL_YEAR_OFFSET_DAYS = 364;

const parseDateValue = (dateValue) => {
  if (!dateValue) return null;
  const parsed = new Date(`${dateValue}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addDays = (dateObj, days) => {
  const shifted = new Date(dateObj);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
};

const formatRangeLabel = (startDate, dayCount, withYear) => {
  if (dayCount <= 1) {
    return startDate.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      ...(withYear ? { year: "numeric" } : {}),
    });
  }

  const endDate = addDays(startDate, dayCount - 1);
  const shortOptions = { month: "short", day: "numeric" };
  const startLabel = startDate.toLocaleDateString("en-US", shortOptions);
  const endLabel = endDate.toLocaleDateString("en-US", shortOptions);
  const yearSuffix = withYear ? `, ${endDate.getFullYear()}` : "";

  return `${startLabel} – ${endLabel}${yearSuffix}`;
};

// Spells out exactly which dates each side of the comparison covers, so the
// baseline is verifiable instead of implied.
const getComparisonPeriods = (startDateValue, dayCount = 1) => {
  const currentStart = parseDateValue(startDateValue);
  if (!currentStart) return { current: null, prior: null };

  return {
    current: formatRangeLabel(currentStart, dayCount, false),
    prior: formatRangeLabel(
      addDays(currentStart, -FISCAL_YEAR_OFFSET_DAYS),
      dayCount,
      true
    ),
  };
};

const getDailyStats = (week) => {
  if (!Array.isArray(week?.daily_stats)) return [];

  return [...week.daily_stats].sort((a, b) =>
    String(a.date || "").localeCompare(String(b.date || ""))
  );
};

// Rebuilds a weekly sales total from the days it was made of.
//
// The backend sums these with a rule that nulls the whole week if any one day
// is null, which is there to stop an unfinished period comparing against a
// complete one. A day the store was closed has no sales row either, so a
// weekday-only location loses its entire week to the same rule. The daily
// figures survive it, so the week is added back up here, skipping the days with
// nothing in them.
//
// Safe on last year because last year is entirely over: a null day there can
// only mean the store was shut, never that data is still arriving. Returns null
// when no day has a figure, so a store with genuinely no history stays absent
// rather than turning into a zero.
//
// `requirePriorYearMatch` is for the comparison fields, where skipping a day is
// only safe if that day is absent from both years. A day with no figure now but
// sales on the matching day last year is a hole in the comparison, not a closed
// store, and summing six days against seven would report a decline the size of
// the missing day. Those weeks withhold instead, exactly as they did before the
// week was rebuilt from its days.
const sumWeekFromDays = (week, field, fallback, requirePriorYearMatch = false) => {
  const days = getDailyStats(week);
  const dayCarriesField = days.some(
    (day) => day && Object.prototype.hasOwnProperty.call(day, field)
  );
  if (!dayCarriesField) return fallback;

  let total = null;
  let withheld = false;
  days.forEach((day) => {
    const value = day?.[field];
    if (!hasFiniteNumber(value)) {
      if (
        requirePriorYearMatch &&
        hasFiniteNumber(day?.prior_year_actual_revenue)
      ) {
        withheld = true;
      }
      return;
    }
    total = (total === null ? 0 : total) + Number(value);
  });

  return withheld ? null : total;
};

// How many days in the week actually carry a figure. Paired across the two
// years this is what shows whether the comparison is even: a store shut on
// Sundays in both years lands on the same count both times and compares
// cleanly, while one that has since started opening weekends does not.
const countWeekDaysWithValue = (week, field) =>
  getDailyStats(week).filter((day) => hasFiniteNumber(day?.[field])).length;

const getForecastAdjustmentDetails = (initialForecast, adjustedForecast) => {
  const initialNumber = toNumber(initialForecast);
  const adjustedNumber = toNumber(adjustedForecast);
  const amount = adjustedNumber - initialNumber;
  const hasAdjustment = Math.abs(amount) >= 1;
  const percent = initialNumber > 0 ? (amount / initialNumber) * 100 : null;

  if (!hasAdjustment) {
    return {
      label: "No adjustment",
      hasAdjustment,
      amount: 0,
      percent: null,
      toneClass: "border-slate-200 bg-slate-100 text-slate-600",
      icon: null,
    };
  }

  const isUp = amount > 0;

  return {
    label: isUp ? "Adjusted up" : "Adjusted down",
    hasAdjustment,
    amount,
    percent,
    toneClass: isUp
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : "border-rose-200 bg-rose-50 text-rose-700",
    icon: isUp ? (
      <TrendingUpIcon sx={{ fontSize: 14 }} />
    ) : (
      <TrendingDownIcon sx={{ fontSize: 14 }} />
    ),
  };
};

const getActualSalesVariance = (actualRevenue, forecastRevenue) => {
  if (!hasFiniteNumber(actualRevenue) || !hasFiniteNumber(forecastRevenue)) {
    return null;
  }

  const forecastNumber = Number(forecastRevenue);
  if (forecastNumber <= 0) return null;

  const variance =
    ((Number(actualRevenue) - forecastNumber) / forecastNumber) * 100;
  return Number.isFinite(variance) ? variance : null;
};

const getSalesCompValue = (source, field, fallback) => {
  if (source && Object.prototype.hasOwnProperty.call(source, field)) {
    return source[field];
  }

  return fallback;
};

// Past two names the list outgrows the tooltip, so it falls back to a count.
const formatMissingPriorYearStores = (names) => {
  if (names.length === 1) return `${names[0]} has`;
  if (names.length === 2) return `${names[0]} and ${names[1]} have`;
  return `${names.length} stores have`;
};

// Explains the two sides of the comparison when they do not match, which is
// never visible from the totals alone: a store that was not trading last year
// is absent from the baseline but present in this year's figure.
//
// Silent when the sides do line up. A caveat on every cell is a caveat nobody
// reads, and it would undercut the comparisons that are exact.
const getComparisonBasisNote = (
  basis,
  priorLabel,
  periodLabel,
  compareDayCounts
) => {
  if (!basis) return null;

  const {
    storeCount = 0,
    priorYearStoreCount = 0,
    missingStoreNames = [],
    priorYearDayCount = null,
    currentYearDayCount = null,
  } = basis;

  // The day counts come off actual sales, so they only mean something once
  // there are actual sales on screen. Part way through a period the current
  // side has only the days elapsed so far, and measuring that against a whole
  // week last year reports a mismatch that is really just the week in progress.
  const hasDayCounts =
    compareDayCounts &&
    hasFiniteNumber(priorYearDayCount) &&
    hasFiniteNumber(currentYearDayCount);
  const unevenDays =
    hasDayCounts && priorYearDayCount !== currentYearDayCount;
  if (missingStoreNames.length === 0 && !unevenDays) return null;

  const parts = [];

  // The store count is always worth stating on a roll-up. Where the days cannot
  // be compared, the stores carry the whole basis, so that phrasing takes over.
  if (storeCount > 1) {
    parts.push(
      !hasDayCounts && priorYearStoreCount < storeCount
        ? `Last year covers ${priorYearStoreCount} of ${storeCount} stores.`
        : `Comparing ${storeCount} stores.`
    );
  }

  if (hasDayCounts) {
    parts.push(
      unevenDays
        ? `Last year covers ${priorYearDayCount} days of sales, ${periodLabel} covers ${currentYearDayCount}.`
        : `Both years cover ${priorYearDayCount} days of sales.`
    );
  }

  if (missingStoreNames.length > 0) {
    parts.push(
      `${formatMissingPriorYearStores(missingStoreNames)} no sales recorded for ${
        priorLabel || "this period last year"
      }.`
    );
  }

  if (parts.length === 0) return null;
  parts.push("The two years are not an exact comparison.");

  return parts.join(" ");
};

function SalesComparisonTooltip({
  comparisonContext,
  periodLabels,
  periodNoun = "period",
  initialForecast,
  initialForecastForComp,
  forecastSales,
  forecastSalesForComp,
  hasForecastAdjustment,
  priorYearActualSales,
  actualSales,
  actualSalesForComp,
  showActualSales,
  actualVsForecastPercent = null,
  goodAdjustments = 0,
  badAdjustments = 0,
  comparisonBasis = null,
}) {
  const hasPriorYearBaseline =
    hasFiniteNumber(priorYearActualSales) && Number(priorYearActualSales) > 0;
  const forecastComp = getSalesCompPercent(
    forecastSalesForComp,
    priorYearActualSales
  );
  const initialForecastComp = getSalesCompPercent(
    initialForecastForComp,
    priorYearActualSales
  );
  const actualComp = showActualSales
    ? getSalesCompPercent(actualSalesForComp, priorYearActualSales)
    : null;

  // A baseline exists but a comp still came back empty, which means the
  // current-period total was incomplete and was withheld on purpose.
  const hasPartialPeriod =
    hasPriorYearBaseline &&
    (forecastComp === null || (showActualSales && actualComp === null));

  // Per-day counts: each completed day is scored on whether the adjusted
  // forecast landed closer to that day's actual than the original did.
  const adjustmentParts = [];
  if (goodAdjustments > 0) adjustmentParts.push(`${goodAdjustments} good`);
  if (badAdjustments > 0) adjustmentParts.push(`${badAdjustments} poor`);

  const forecastLabel = hasForecastAdjustment
    ? "Adjusted forecast"
    : "Rightwork forecast";
  // "Rightwork" keeps its capital inside a sentence; only the generic label
  // gets lowercased.
  const forecastLabelInline = hasForecastAdjustment
    ? "adjusted forecast"
    : "Rightwork forecast";
  const priorLabel = periodLabels?.prior;
  const currentLabel = periodLabels?.current;
  const subtitle =
    currentLabel && priorLabel
      ? `${currentLabel} compared with ${priorLabel}`
      : comparisonContext;

  // Null unless the two sides of the comparison actually differ.
  const basisNote = hasPriorYearBaseline
    ? getComparisonBasisNote(
        comparisonBasis,
        priorLabel,
        `this ${periodNoun}`,
        showActualSales
      )
    : null;

  return (
    <TooltipCard className={TOOLTIP_WIDTH_CLASS}>
      <TooltipHeader title="Sales vs last year" subtitle={subtitle} />

      {/* Last year's actual is the first row and the delta column is labelled
          once, so every percentage below is visibly measured against it. */}
      <CompareHeader
        amountLabel="Sales"
        deltaLabel="vs last year"
        showDelta={hasPriorYearBaseline}
      />

      {/* The date range lives in the subtitle, where it sits alongside the
          current period and shows the 52-week pairing. Repeating it here
          would state the same fact twice. */}
      <CompareRow
        label="Last year actual"
        amount={formatOptionalCurrency(priorYearActualSales)}
        showDelta={hasPriorYearBaseline}
        isBaseline
      />

      {/* Only a distinct row once an adjustment exists; without one it is the
          same number as the forecast row below. */}
      {hasForecastAdjustment ? (
        <CompareRow
          label="Rightwork forecast"
          amount={formatOptionalCurrency(initialForecast)}
          delta={initialForecastComp}
          deltaPlaceholder="Partial"
          showDelta={hasPriorYearBaseline}
        />
      ) : null}

      <CompareRow
        label={forecastLabel}
        amount={formatOptionalCurrency(forecastSales)}
        delta={forecastComp}
        deltaPlaceholder="Partial"
        showDelta={hasPriorYearBaseline}
      />

      {showActualSales ? (
        <CompareRow
          label="Actual sales"
          amount={formatOptionalCurrency(actualSales)}
          delta={actualComp}
          deltaPlaceholder="Partial"
          showDelta={hasPriorYearBaseline}
          emphasis
        />
      ) : null}

      {basisNote ? (
        <div className="mt-2">
          <TooltipNote tone="warn">{basisNote}</TooltipNote>
        </div>
      ) : null}

      {!hasPriorYearBaseline ? (
        <div className="mt-2">
          <TooltipNote>
            No sales recorded for {priorLabel || "this period last year"}, so
            there is nothing to compare against yet.
          </TooltipNote>
        </div>
      ) : null}

      {hasPartialPeriod ? (
        <div className="mt-2">
          <TooltipNote tone="warn">
            Sales for this {periodNoun} are still coming in. Comparisons are
            held back until it is complete.
          </TooltipNote>
        </div>
      ) : null}

      {showActualSales || hasForecastAdjustment ? (
        <>
          <TooltipDivider />
          {/* A different frame of reference, so it gets its own heading and
              flows left instead of landing under the vs-last-year column. */}
          <TooltipSectionLabel>Against the plan</TooltipSectionLabel>

          {showActualSales ? (
            <p className="px-2 text-[11px] leading-relaxed text-slate-600">
              {actualVsForecastPercent === null ? (
                "No forecast comparison available for this period."
              ) : actualVsForecastPercent === 0 ? (
                <>
                  Actual sales landed in line with the{" "}
                  {forecastLabelInline} of{" "}
                  {formatOptionalCurrency(forecastSales)}.
                </>
              ) : (
                <>
                  Actual sales came in{" "}
                  <DeltaValue value={actualVsForecastPercent} size="inline" />{" "}
                  {actualVsForecastPercent > 0 ? "above" : "below"} the{" "}
                  {forecastLabelInline} of{" "}
                  {formatOptionalCurrency(forecastSales)}.
                </>
              )}
            </p>
          ) : null}

          {hasForecastAdjustment ? (
            <p className="mt-1.5 px-2 text-[11px] leading-relaxed text-slate-400">
              Forecast was adjusted from{" "}
              {formatOptionalCurrency(initialForecast)} to{" "}
              {formatOptionalCurrency(forecastSales)}
              {adjustmentParts.length > 0
                ? ` (${adjustmentParts.join(" · ")})`
                : ""}
              .
            </p>
          ) : null}
        </>
      ) : null}
    </TooltipCard>
  );
}

const getLaborMetricSales = (source, field, fallback, showLaborPercent) => {
  if (
    showLaborPercent &&
    source &&
    Object.prototype.hasOwnProperty.call(source, field)
  ) {
    return source[field];
  }

  return fallback;
};

// Builds one section descriptor: the tooltip content plus the props for the
// progress row it belongs to.
function buildDayLaborSection({
  label,
  actual,
  target,
  targetLabel,
  score,
  muted = false,
  sales,
  salesLabel,
  valueLabel,
  targetMetricLabel,
  valueLaborCost,
  targetLaborCost,
}) {
  const metricValueLabel = valueLabel || label;
  const metricTargetLabel = targetMetricLabel || capitalizeLabel(targetLabel);

  return {
    heading: label,
    salesLabel,
    sales,
    valueLabel: metricValueLabel,
    valueHours: actual,
    valueLaborCost,
    targetLabel: metricTargetLabel,
    targetHours: target,
    targetLaborCost,
    row: {
      label,
      actual,
      target,
      targetLabel,
      score,
      muted,
    },
  };
}

// Mirrors WeekLaborSummary: one shared tooltip once the day has both a
// planned and an actual row.
function DayLaborBlock({ sections, showLaborPercent }) {
  const rows = sections.map((section) => (
    <LaborProgressSummary
      key={section.heading}
      {...section.row}
      barClassName="min-w-0 flex-1"
    />
  ));

  return (
    <OverviewTooltip
      title={
        <LaborTooltip sections={sections} showLaborPercent={showLaborPercent} />
      }
    >
      <div className="cursor-pointer space-y-3">{rows}</div>
    </OverviewTooltip>
  );
}

function WeekLaborSummary({
  week,
  scheduledHours,
  forecastedHours,
  actualHours,
  earnedHours,
  forecastSales,
  forecastSalesLabel,
  showLaborPercent,
}) {
  const plannedMetricSales = getLaborMetricSales(
    week,
    "forecasted_revenue_adjustment_for_labor_percent",
    forecastSales,
    showLaborPercent
  );
  const actualMetricSales = getLaborMetricSales(
    week,
    "actual_revenue_for_labor_percent",
    week?.actual_revenue,
    showLaborPercent
  );
  const plannedSection = {
    heading: "Planned",
    salesLabel: forecastSalesLabel,
    sales: plannedMetricSales,
    valueLabel: "Scheduled",
    valueHours: scheduledHours,
    valueLaborCost: week?.scheduled_labor_cost,
    targetLabel: "Forecasted",
    targetHours: forecastedHours,
    targetLaborCost: week?.forecasted_labor_cost,
  };
  const actualSection = {
    heading: "Actual",
    salesLabel: "Actual Sales",
    sales: actualMetricSales,
    valueLabel: "Actual",
    valueHours: actualHours,
    valueLaborCost: week?.labor_cost,
    targetLabel: "Earned",
    targetHours: earnedHours,
    targetLaborCost: week?.earned_labor_cost,
  };

  // In-progress weeks only render the planned row, so there is nothing to
  // merge and the tooltip stays on the bar itself.
  if (!week?.has_completed_week) {
    return (
      <LaborProgressSummary
        label="Planned"
        actual={scheduledHours}
        target={forecastedHours}
        targetLabel="forecasted"
        score={week?.planned_score}
        tooltipTitle={
          <LaborTooltip
            sections={[plannedSection]}
            showLaborPercent={showLaborPercent}
          />
        }
      />
    );
  }

  return (
    <OverviewTooltip
      title={
        <LaborTooltip
          sections={[plannedSection, actualSection]}
          showLaborPercent={showLaborPercent}
        />
      }
    >
      <div className="cursor-pointer space-y-2.5">
        <LaborProgressSummary
          label="Planned"
          actual={scheduledHours}
          target={forecastedHours}
          targetLabel="forecasted"
          score={week?.planned_score}
          muted
        />
        <LaborProgressSummary
          label="Actual"
          actual={actualHours}
          target={earnedHours}
          targetLabel="earned"
          score={week?.actual_score}
        />
      </div>
    </OverviewTooltip>
  );
}

function WeeklySalesSummary({ week, showUnpublished = false }) {
  const initialForecast = week?.forecasted_revenue;
  const adjustedForecast =
    week?.forecasted_revenue_adjustment ?? week?.forecasted_revenue;
  const forecastAdjustment = getForecastAdjustmentDetails(
    initialForecast,
    adjustedForecast
  );
  const forecastSales = forecastAdjustment.hasAdjustment
    ? adjustedForecast
    : initialForecast;
  // Rebuilt from the daily figures so a day the store was closed does not take
  // the whole week's comparison down with it.
  const priorYearActualSales = sumWeekFromDays(
    week,
    "prior_year_actual_revenue",
    week?.prior_year_actual_revenue
  );
  const forecastSalesForComp = sumWeekFromDays(
    week,
    forecastAdjustment.hasAdjustment
      ? "forecasted_revenue_adjustment_for_sales_comp"
      : "forecasted_revenue_for_sales_comp",
    forecastSales,
    true
  );
  const initialForecastForComp = sumWeekFromDays(
    week,
    "forecasted_revenue_for_sales_comp",
    initialForecast,
    true
  );
  const actualSalesForComp = sumWeekFromDays(
    week,
    "actual_revenue_for_sales_comp",
    week?.actual_revenue,
    true
  );
  const showActualSales = Boolean(week?.has_completed_week);
  // The roll-up builds its own basis across stores. A single store still gets
  // one, so its own change in opening days is disclosed the same way.
  const comparisonBasis =
    week?.sales_comparison_basis ||
    (getDailyStats(week).length > 0
      ? {
          storeCount: 1,
          priorYearStoreCount: hasFiniteNumber(priorYearActualSales) ? 1 : 0,
          missingStoreNames: [],
          priorYearDayCount: countWeekDaysWithValue(
            week,
            "prior_year_actual_revenue"
          ),
          currentYearDayCount: countWeekDaysWithValue(week, "actual_revenue"),
        }
      : null);
  const actualSalesVariance = getActualSalesVariance(
    week?.actual_revenue,
    forecastSales
  );
  const actualVarianceClass =
    actualSalesVariance === null || actualSalesVariance === 0
      ? "text-slate-500"
      : actualSalesVariance > 0
      ? "text-emerald-700"
      : "text-rose-700";
  const tooltipTitle = (
    <SalesComparisonTooltip
      comparisonContext="Same fiscal week last year"
      periodNoun="week"
      periodLabels={getComparisonPeriods(
        week?.week_start,
        getDailyStats(week).length || 7
      )}
      actualVsForecastPercent={actualSalesVariance}
      initialForecast={initialForecast}
      initialForecastForComp={initialForecastForComp}
      forecastSales={forecastSales}
      forecastSalesForComp={forecastSalesForComp}
      hasForecastAdjustment={forecastAdjustment.hasAdjustment}
      priorYearActualSales={priorYearActualSales}
      actualSales={week?.actual_revenue}
      actualSalesForComp={actualSalesForComp}
      showActualSales={showActualSales}
      goodAdjustments={toNumber(week?.good_adjustments)}
      badAdjustments={toNumber(week?.bad_adjustments)}
      comparisonBasis={comparisonBasis}
    />
  );

  return (
    <OverviewTooltip title={tooltipTitle}>
      {/* The whole row shares one tooltip: forecast and actual are two halves
          of the same comparison, so splitting the hover target only made it
          harder to reach. */}
      <div className="mb-3 flex cursor-pointer items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="mb-0.5 truncate text-xs font-medium text-slate-500">
            {forecastAdjustment.hasAdjustment
              ? "Adjusted Forecast"
              : "Forecasted Sales"}
          </p>
          <div className="flex items-center gap-1">
            <p className="truncate text-base font-semibold text-slate-900">
              {formatOptionalCurrency(forecastSales)}
            </p>
            {forecastAdjustment.hasAdjustment && (
              <span className="flex shrink-0 text-slate-900">
                {forecastAdjustment.icon}
              </span>
            )}
          </div>
        </div>

        {showActualSales ? (
          <div className="shrink-0 text-right">
            <p className="mb-0.5 text-xs font-medium text-slate-500">
              Actual Sales
            </p>
            <div className="flex items-center justify-end gap-2">
              {actualSalesVariance !== null && (
                <div className={`flex items-center gap-0.5 ${actualVarianceClass}`}>
                  {actualSalesVariance > 0 && (
                    <NorthIcon style={{ fontSize: "12px" }} />
                  )}
                  {actualSalesVariance < 0 && (
                    <SouthIcon style={{ fontSize: "12px" }} />
                  )}
                  <p className="text-xs font-medium">
                    {Math.abs(actualSalesVariance).toFixed(1)}%
                  </p>
                </div>
              )}
              <p className="text-base font-semibold text-slate-900">
                {formatOptionalCurrency(week?.actual_revenue)}
              </p>
            </div>
          </div>
        ) : (
          <div>
            {showUnpublished && week?.published_shifts_count === 0 && (
              <OpsChip label="Unpublished" />
            )}
          </div>
        )}
      </div>
    </OverviewTooltip>
  );
}

function DailyWeekColumn({ day, hasCompletedWeek, showLaborPercent }) {
  const adjustedForecast = day?.forecasted_revenue_adjustment ?? day?.forecasted_revenue;
  const rawActualRevenue = day?.actual_revenue;
  const actualRevenue = toNumber(rawActualRevenue);
  const adjustedForecastNumber = toNumber(adjustedForecast);
  const initialForecastNumber = toNumber(day?.forecasted_revenue);
  const forecastAdjustment = getForecastAdjustmentDetails(
    initialForecastNumber,
    adjustedForecastNumber
  );
  const displayedForecastNumber = forecastAdjustment.hasAdjustment
    ? adjustedForecastNumber
    : initialForecastNumber;
  const plannedMetricSales = getLaborMetricSales(
    day,
    "forecasted_revenue_adjustment_for_labor_percent",
    displayedForecastNumber,
    showLaborPercent
  );
  const actualMetricSales = getLaborMetricSales(
    day,
    "actual_revenue_for_labor_percent",
    actualRevenue,
    showLaborPercent
  );
  const actualSalesVariance = getActualSalesVariance(
    rawActualRevenue,
    displayedForecastNumber
  );
  const actualVarianceClass =
    actualSalesVariance === null || actualSalesVariance === 0
      ? "text-slate-500"
      : actualSalesVariance > 0
      ? "text-emerald-700"
      : "text-rose-700";
  const hasActualRevenue =
    rawActualRevenue !== null &&
    rawActualRevenue !== undefined &&
    rawActualRevenue !== "" &&
    Number.isFinite(Number(rawActualRevenue));
  const showActualSales = hasCompletedWeek || hasActualRevenue;
  const forecastSalesForComp = getSalesCompValue(
    day,
    forecastAdjustment.hasAdjustment
      ? "forecasted_revenue_adjustment_for_sales_comp"
      : "forecasted_revenue_for_sales_comp",
    displayedForecastNumber
  );
  const initialForecastForComp = getSalesCompValue(
    day,
    "forecasted_revenue_for_sales_comp",
    initialForecastNumber
  );
  const actualSalesForComp = getSalesCompValue(
    day,
    "actual_revenue_for_sales_comp",
    rawActualRevenue
  );
  const showActualLabor =
    hasCompletedWeek ||
    toNumber(day?.actual_labor_hours) > 0 ||
    toNumber(day?.earned_labor_hours) > 0;
  const goodAdjustments = getAdjustmentCount(
    day,
    "good_adjustment",
    "good_adjustments"
  );
  const badAdjustments = getAdjustmentCount(
    day,
    "bad_adjustment",
    "bad_adjustments"
  );
  const autoClockOuts = toNumber(day?.auto_clock_out_count);
  const missedBreaks = toNumber(day?.missed_break_count);
  const hasOpsIssues = autoClockOuts > 0 || missedBreaks > 0;
  const salesTooltipTitle = (
    <SalesComparisonTooltip
      comparisonContext="Same weekday last year"
      periodNoun="day"
      periodLabels={getComparisonPeriods(day?.date, 1)}
      actualVsForecastPercent={actualSalesVariance}
      initialForecast={initialForecastNumber}
      initialForecastForComp={initialForecastForComp}
      forecastSales={displayedForecastNumber}
      forecastSalesForComp={forecastSalesForComp}
      hasForecastAdjustment={forecastAdjustment.hasAdjustment}
      priorYearActualSales={day?.prior_year_actual_revenue}
      actualSales={rawActualRevenue}
      actualSalesForComp={actualSalesForComp}
      showActualSales={showActualSales}
      goodAdjustments={goodAdjustments}
      badAdjustments={badAdjustments}
      comparisonBasis={day?.sales_comparison_basis}
    />
  );

  return (
    <div className="flex min-w-[260px] flex-col rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-3 py-2.5">
        <p className="truncate text-sm font-bold text-slate-900">
          {formatDayLabel(day?.date)}
        </p>
        <p className="truncate text-xs font-medium text-slate-500">
          {formatDateReadable(day?.date)}
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-3">
        <OverviewTooltip title={salesTooltipTitle}>
          <div className="flex min-h-[54px] cursor-pointer items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="mb-0.5 truncate text-xs font-medium text-slate-500">
                {forecastAdjustment.hasAdjustment
                  ? "Adjusted"
                  : "Forecasted"}
              </p>
              <div className="flex items-center gap-1">
                <p className="truncate text-base font-semibold text-slate-900">
                  {formatCurrency(displayedForecastNumber)}
                </p>
                {forecastAdjustment.hasAdjustment && (
                  <span className="flex shrink-0 text-slate-900">
                    {forecastAdjustment.icon}
                  </span>
                )}
              </div>
            </div>

            {showActualSales ? (
              <div className="shrink-0 text-right">
                <p className="mb-0.5 text-xs font-medium text-slate-500">
                  Actual
                </p>
                <div className="flex items-center justify-end gap-1.5">
                  {actualSalesVariance !== null && (
                    <div
                      className={`flex items-center gap-0.5 ${actualVarianceClass}`}
                    >
                      {actualSalesVariance > 0 && (
                        <NorthIcon style={{ fontSize: "12px" }} />
                      )}
                      {actualSalesVariance < 0 && (
                        <SouthIcon style={{ fontSize: "12px" }} />
                      )}
                      <p className="text-xs font-medium">
                        {Math.abs(actualSalesVariance).toFixed(1)}%
                      </p>
                    </div>
                  )}
                  <p className="text-base font-semibold text-slate-900">
                    {formatCurrency(actualRevenue)}
                  </p>
                </div>
              </div>
            ) : (
              <div />
            )}
          </div>
        </OverviewTooltip>

        <DayLaborBlock
          showLaborPercent={showLaborPercent}
          sections={[
            buildDayLaborSection({
              label: "Planned",
              actual: day?.scheduled_labor_hours,
              target: day?.forecasted_labor_hours,
              targetLabel: "forecasted",
              score: day?.planned_score,
              muted: showActualLabor,
              sales: plannedMetricSales,
              salesLabel: forecastAdjustment.hasAdjustment
                ? "Adjusted Forecast"
                : "Forecasted Sales",
              valueLabel: "Scheduled",
              targetMetricLabel: "Forecasted",
              valueLaborCost: day?.scheduled_labor_cost,
              targetLaborCost: day?.forecasted_labor_cost,
            }),
            ...(showActualLabor
              ? [
                  buildDayLaborSection({
                    label: "Actual",
                    actual: day?.actual_labor_hours,
                    target: day?.earned_labor_hours,
                    targetLabel: "earned",
                    score: day?.actual_score,
                    sales: actualMetricSales,
                    salesLabel: "Actual Sales",
                    valueLabel: "Actual",
                    targetMetricLabel: "Earned",
                    valueLaborCost: day?.labor_cost,
                    targetLaborCost: day?.earned_labor_cost,
                  }),
                ]
              : []),
          ]}
        />

        {hasOpsIssues && (
          <div className="mt-auto flex flex-wrap gap-1">
            {autoClockOuts > 0 && (
              <OpsChip label="Auto clock-outs" value={autoClockOuts} />
            )}
            {missedBreaks > 0 && (
              <OpsChip label="Missed breaks" value={missedBreaks} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function WeekDetailModal({ detail, onClose, onOpenSchedule, showLaborPercent }) {
  const isOpen = Boolean(detail?.week);

  useEffect(() => {
    if (
      !isOpen ||
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const scrollY = window.scrollY;
    const bodyStyle = document.body.style;
    const htmlStyle = document.documentElement.style;
    const previousBodyStyles = {
      overflow: bodyStyle.overflow,
      position: bodyStyle.position,
      top: bodyStyle.top,
      width: bodyStyle.width,
      overscrollBehavior: bodyStyle.overscrollBehavior,
    };
    const previousHtmlOverscrollBehavior = htmlStyle.overscrollBehavior;

    bodyStyle.overflow = "hidden";
    bodyStyle.position = "fixed";
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.width = "100%";
    bodyStyle.overscrollBehavior = "contain";
    htmlStyle.overscrollBehavior = "contain";

    return () => {
      bodyStyle.overflow = previousBodyStyles.overflow;
      bodyStyle.position = previousBodyStyles.position;
      bodyStyle.top = previousBodyStyles.top;
      bodyStyle.width = previousBodyStyles.width;
      bodyStyle.overscrollBehavior = previousBodyStyles.overscrollBehavior;
      htmlStyle.overscrollBehavior = previousHtmlOverscrollBehavior;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const { week, storeName, canOpenSchedule } = detail;
  const detailShowLaborPercent = Boolean(
    detail?.showLaborPercent ?? showLaborPercent
  );
  const dailyStats = getDailyStats(week);

  const closeOnBackdropClick = (event) => {
    if (event.currentTarget === event.target) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center overscroll-contain bg-slate-900/40 px-5 py-6"
      onMouseDown={closeOnBackdropClick}
      role="presentation"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-[96vw] flex-col overflow-hidden rounded-lg bg-slate-50 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="week-detail-modal-title"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-500">
              {storeName}
            </p>
            <h2
              id="week-detail-modal-title"
              className="mt-1 text-xl font-bold text-slate-950"
            >
              Week review
            </h2>
            <p className="mt-1 text-sm font-medium text-slate-500">
              {formatDateReadable(week.start_date)} - {formatDateReadable(week.end_date)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500"
            aria-label="Close week review"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        <div className="overflow-y-auto overscroll-contain px-5 py-4">
          <div>
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">
                Daily detail
              </h3>
              <p className="text-xs font-semibold text-slate-500">
                {dailyStats.length} days
              </p>
            </div>
            <div className="mt-2 overflow-x-auto pb-2">
              {dailyStats.length > 0 ? (
                <div
                  className="grid min-w-[1820px] gap-2"
                  style={{
                    gridTemplateColumns: `repeat(${dailyStats.length}, minmax(260px, 1fr))`,
                  }}
                >
                  {dailyStats.map((day) => (
                    <DailyWeekColumn
                      key={day.date}
                      day={day}
                      hasCompletedWeek={week.has_completed_week}
                      showLaborPercent={detailShowLaborPercent}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-white px-4 py-8 text-center">
                  <p className="text-sm font-semibold text-slate-700">
                    Daily details are not available for this week.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            Close
          </button>
          {canOpenSchedule && (
            <button
              type="button"
              onClick={onOpenSchedule}
              className="inline-flex items-center gap-2 rounded-md bg-violet-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
            >
              Open schedule
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function OverviewScheduleLoadingState() {
  return (
    <div className="flex min-h-[calc(100vh-160px)] w-full items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex h-8 w-8 items-center justify-center">
          <LoadSpinner />
        </div>
        <p className="text-sm font-medium text-slate-500">
          Loading schedule...
        </p>
      </div>
    </div>
  );
}

function SkeletonWeekCell({ isPast = false }) {
  return (
    <div className={`flex-1 p-3 border-l border-slate-200 ${isPast ? "bg-slate-50" : ""}`}>
      <div className="animate-pulse">
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="h-4 w-24 bg-slate-200 rounded mb-0.5"></div>
            <div className="h-6 w-20 bg-slate-200 rounded"></div>
          </div>
          {isPast && (
            <div className="flex flex-col items-end">
              <div className="h-4 w-16 bg-slate-200 rounded mb-0.5"></div>
              <div className="h-6 w-16 bg-slate-200 rounded"></div>
            </div>
          )}
        </div>
        {isPast ? (
          <div className="space-y-2.5">
            <div className="flex justify-between items-end">
              <div className="w-[75%]">
                <div className="flex justify-between mb-1">
                  <div className="h-4 w-12 bg-slate-200 rounded"></div>
                  <div className="h-4 w-28 bg-slate-200 rounded"></div>
                </div>
                <div className="h-1.5 w-full bg-slate-200 rounded-full"></div>
                <div className="h-5 w-28 bg-slate-200 rounded mt-1"></div>
              </div>
              <div className="w-8 h-8 bg-slate-200 rounded-lg"></div>
            </div>
            <div className="flex justify-between items-end">
              <div className="w-[75%]">
                <div className="flex justify-between mb-1">
                  <div className="h-4 w-10 bg-slate-200 rounded"></div>
                  <div className="h-4 w-28 bg-slate-200 rounded"></div>
                </div>
                <div className="h-1.5 w-full bg-slate-200 rounded-full"></div>
                <div className="h-5 w-28 bg-slate-200 rounded mt-1"></div>
              </div>
              <div className="w-8 h-8 bg-slate-200 rounded-lg"></div>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-end">
            <div className="w-[75%]">
              <div className="flex justify-between mb-1.5">
                <div className="h-4 w-12 bg-slate-200 rounded"></div>
                <div className="h-4 w-28 bg-slate-200 rounded"></div>
              </div>
              <div className="h-1.5 w-full bg-slate-200 rounded-full"></div>
              <div className="h-5 w-28 bg-slate-200 rounded mt-1"></div>
            </div>
            <div className="w-8 h-8 bg-slate-200 rounded-lg"></div>
          </div>
        )}
      </div>
    </div>
  );
}

function FourWeekOverview({
  overviewData,
  weekOffset = -1,
  handlePreviousWeeks,
  handleNextWeeks,
  isLoading = false,
  ...props
}) {
  const [searchInput, setSearchInput] = useState("");
  const [isOpeningSchedule, setIsOpeningSchedule] = useState(false);
  const [selectedWeekDetail, setSelectedWeekDetail] = useState(null);
  const canOpenStoreSchedule = props.schedulingEnabled !== false;

  const openWeekDetail = (detail) => {
    if (!detail?.week) return;
    setSelectedWeekDetail(detail);
  };

  const handleWeekDetailKeyDown = (event, detail) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openWeekDetail(detail);
  };

  const handleWeekCellClick = async (storeId, weekStart) => {
    if (!canOpenStoreSchedule || !weekStart || isOpeningSchedule) {
      return;
    }

    const targetStoreId = Number(storeId);
    const currentStoreId = Number(props.homeStore);

    if (!Number.isFinite(targetStoreId)) {
      return;
    }

    setIsOpeningSchedule(true);

    try {
      await waitForOverviewLoadingPaint();
      if (targetStoreId !== currentStoreId) {
        await props.change_home_store(targetStoreId);
      }
      props.setOverviewScheduleNavigationTarget?.({
        storeId: targetStoreId,
        weekStart,
      });
      props.setScheduleRenderDate?.(weekStart);
      props.setCurrentTab("home");
    } catch (error) {
      console.error("Error opening schedule from overview:", error);
      props.setOverviewScheduleNavigationTarget?.(null);
    } finally {
      setIsOpeningSchedule(false);
    }
  };

  const handleOpenScheduleFromModal = () => {
    if (!selectedWeekDetail?.canOpenSchedule) {
      return;
    }

    const storeId = selectedWeekDetail.storeId;
    const weekStart = selectedWeekDetail.week?.week_start;
    setSelectedWeekDetail(null);
    handleWeekCellClick(storeId, weekStart);
  };

  // Function to filter data based on search input
  const filteredData = overviewData.filter((store) =>
    store.store_name.toLowerCase().includes(searchInput.toLowerCase())
  );
  const allStoresShowLaborPercent =
    overviewData.length > 0 &&
    overviewData.every((store) => Boolean(store?.labor_percent_enabled));

  // Aggregate weekly stats across all stores
  const aggregatedWeeklyStats = useMemo(() => {
    if (!overviewData || overviewData.length <= 1) return null;
    const weekCount = overviewData[0]?.weekly_stats?.length || 0;
    if (weekCount === 0) return null;

    return Array.from({ length: weekCount }, (_, weekIndex) => {
      let totalScheduled = 0;
      let totalForecasted = 0;
      let totalActual = 0;
      let totalEarned = 0;
      const totalLaborCost = createNullableAggregate();
      const totalScheduledLaborCost = createNullableAggregate();
      const totalForecastedLaborCost = createNullableAggregate();
      const totalEarnedLaborCost = createNullableAggregate();
      let totalForecastedRevenue = 0;
      let totalForecastedRevenueAdjustment = 0;
      let totalActualRevenue = 0;
      const totalPriorYearActualRevenue = createNullableAggregate();
      const totalForecastedRevenueForSalesComp = createNullableAggregate();
      const totalForecastedRevenueAdjustmentForSalesComp =
        createNullableAggregate();
      const totalActualRevenueForSalesComp = createNullableAggregate();
      const totalForecastedRevenueForLaborPercent = createNullableAggregate();
      const totalForecastedRevenueAdjustmentForLaborPercent =
        createNullableAggregate();
      const totalActualRevenueForLaborPercent = createNullableAggregate();
      let totalPlannedScore = 0;
      let plannedScoreCount = 0;
      let totalActualScore = 0;
      let actualScoreCount = 0;
      let totalGoodAdjustments = 0;
      let totalBadAdjustments = 0;
      let totalPublishedShifts = 0;
      let totalAutoClockOuts = 0;
      let totalMissedBreaks = 0;
      let totalMissedPaidBreaks = 0;
      let totalMissedMealBreaks = 0;
      // Completion is per store now that it is derived from each store's own
      // timezone and business-day boundary, so the aggregate is only complete
      // when every contributing store is. Taking the first store's flag could
      // show actual sales and comps built on another store's partial week.
      let contributingStoreCount = 0;
      let allStoresCompletedWeek = true;
      // Stores with nothing on the books for this week last year. Last year's
      // total is still worth showing without them, but it covers fewer stores
      // than the current-year rows beside it, so the tooltip has to say so.
      const priorYearMissingStoreNames = [];
      // Stores trading this week, which is what the comparison is actually
      // built from. A store shut all week is in neither side.
      let tradingStoreCount = 0;
      // Store-days, not days: 14 stores trading 5 days each is 70. Comparing
      // the two years on this is what reveals a store that has changed which
      // days it opens, which no total makes visible.
      let priorYearDayCount = 0;
      let currentYearDayCount = 0;
      const dailyTotalsByDate = {};

      overviewData.forEach((store) => {
        const week = store.weekly_stats[weekIndex];
        if (!week) return;
        contributingStoreCount += 1;
        if (!week.has_completed_week) allStoresCompletedWeek = false;
        totalScheduled += week.scheduled_labor_hours || 0;
        totalForecasted += week.forecasted_labor_hours || 0;
        totalActual += week.actual_labor_hours || 0;
        totalEarned += week.earned_labor_hours || 0;
        addNullableAggregateValue(totalLaborCost, week.labor_cost);
        addNullableAggregateValue(
          totalScheduledLaborCost,
          week.scheduled_labor_cost
        );
        addNullableAggregateValue(
          totalForecastedLaborCost,
          week.forecasted_labor_cost
        );
        addNullableAggregateValue(totalEarnedLaborCost, week.earned_labor_cost);
        totalForecastedRevenue += toNumber(week.forecasted_revenue);
        totalForecastedRevenueAdjustment += toNumber(
          week.forecasted_revenue_adjustment
        );
        totalActualRevenue += toNumber(week.actual_revenue);
        // Rebuilt from the days before rolling up, so a store closed at
        // weekends contributes its real weekday total instead of arriving as a
        // null and blanking the comparison for every other store.
        const storePriorYearActual = sumWeekFromDays(
          week,
          "prior_year_actual_revenue",
          week.prior_year_actual_revenue
        );
        addNullableAggregateValue(
          totalPriorYearActualRevenue,
          storePriorYearActual
        );
        const storeActualDays = countWeekDaysWithValue(week, "actual_revenue");
        // Forecast days keep a future week counted, where no actual exists yet.
        const storeTradesThisWeek =
          storeActualDays > 0 ||
          countWeekDaysWithValue(week, "forecasted_revenue") > 0;
        if (storeTradesThisWeek) {
          tradingStoreCount += 1;
          // Only a store trading now can skew the comparison by having no
          // history. One shut all week is absent from both years and is no part
          // of the comparison either way.
          if (!hasFiniteNumber(storePriorYearActual)) {
            priorYearMissingStoreNames.push(store.store_name);
          }
        }
        priorYearDayCount += countWeekDaysWithValue(
          week,
          "prior_year_actual_revenue"
        );
        currentYearDayCount += storeActualDays;
        addComparisonValue(
          totalForecastedRevenueForSalesComp,
          sumWeekFromDays(
            week,
            "forecasted_revenue_for_sales_comp",
            week.forecasted_revenue,
            true
          ),
          storePriorYearActual
        );
        addComparisonValue(
          totalForecastedRevenueAdjustmentForSalesComp,
          sumWeekFromDays(
            week,
            "forecasted_revenue_adjustment_for_sales_comp",
            week.forecasted_revenue_adjustment,
            true
          ),
          storePriorYearActual
        );
        addComparisonValue(
          totalActualRevenueForSalesComp,
          sumWeekFromDays(
            week,
            "actual_revenue_for_sales_comp",
            week.actual_revenue,
            true
          ),
          storePriorYearActual
        );
        addNullableAggregateValue(
          totalForecastedRevenueForLaborPercent,
          getLaborMetricSales(
            week,
            "forecasted_revenue_for_labor_percent",
            week.forecasted_revenue,
            allStoresShowLaborPercent
          )
        );
        addNullableAggregateValue(
          totalForecastedRevenueAdjustmentForLaborPercent,
          getLaborMetricSales(
            week,
            "forecasted_revenue_adjustment_for_labor_percent",
            week.forecasted_revenue_adjustment,
            allStoresShowLaborPercent
          )
        );
        addNullableAggregateValue(
          totalActualRevenueForLaborPercent,
          getLaborMetricSales(
            week,
            "actual_revenue_for_labor_percent",
            week.actual_revenue,
            allStoresShowLaborPercent
          )
        );
        if (week.planned_score_eligible ?? Boolean(week.planned_score)) {
          totalPlannedScore += week.planned_score;
          plannedScoreCount++;
        }
        if (week.actual_score_eligible ?? Boolean(week.actual_score)) {
          totalActualScore += week.actual_score;
          actualScoreCount++;
        }
        totalGoodAdjustments += week.good_adjustments || 0;
        totalBadAdjustments += week.bad_adjustments || 0;
        totalPublishedShifts += week.published_shifts_count || 0;
        totalAutoClockOuts += week.auto_clock_out_count || 0;
        totalMissedBreaks += week.missed_break_count || 0;
        totalMissedPaidBreaks += week.missed_paid_break_count || 0;
        totalMissedMealBreaks += week.missed_meal_break_count || 0;

        const weekDailyStats = Array.isArray(week.daily_stats) ? week.daily_stats : [];
        weekDailyStats.forEach((day) => {
          if (!day?.date) return;
          if (!dailyTotalsByDate[day.date]) {
            dailyTotalsByDate[day.date] = {
              date: day.date,
              actual_revenue: 0,
              prior_year_actual_revenue: createNullableAggregate(),
              // Coverage is per date, not once for the roll-up: a store that
              // opened partway through last year has a prior-year figure on
              // some days and not others.
              //
              // Counts only stores trading on this date. A store shut on
              // Sundays is absent from both years, so it is no part of the
              // comparison and naming it would report a mismatch that is really
              // just a closed store.
              tradingStoreCount: 0,
              priorYearMissingStoreNames: [],
              forecasted_revenue: 0,
              forecasted_revenue_adjustment: 0,
              actual_revenue_for_sales_comp: createNullableAggregate(),
              forecasted_revenue_for_sales_comp: createNullableAggregate(),
              forecasted_revenue_adjustment_for_sales_comp:
                createNullableAggregate(),
              actual_revenue_for_labor_percent: createNullableAggregate(),
              forecasted_revenue_for_labor_percent: createNullableAggregate(),
              forecasted_revenue_adjustment_for_labor_percent:
                createNullableAggregate(),
              labor_cost: createNullableAggregate(),
              scheduled_labor_cost: createNullableAggregate(),
              forecasted_labor_cost: createNullableAggregate(),
              earned_labor_cost: createNullableAggregate(),
              good_adjustment: 0,
              bad_adjustment: 0,
              scheduled_labor_hours: 0,
              forecasted_labor_hours: 0,
              actual_labor_hours: 0,
              earned_labor_hours: 0,
              published_shifts_count: 0,
              auto_clock_out_count: 0,
              missed_break_count: 0,
              missed_paid_break_count: 0,
              missed_meal_break_count: 0,
              plannedScoreTotal: 0,
              plannedScoreCount: 0,
              actualScoreTotal: 0,
              actualScoreCount: 0,
            };
          }

          const dailyTotal = dailyTotalsByDate[day.date];
          dailyTotal.actual_revenue += toNumber(day.actual_revenue);
          addNullableAggregateValue(
            dailyTotal.prior_year_actual_revenue,
            day.prior_year_actual_revenue
          );
          // Only a store trading this year can skew the comparison by having no
          // history: it lands in this year's total with nothing to match it in
          // the baseline. A store trading last year but not now goes the other
          // way and is already handled by withholding the comp outright, so it
          // needs no note.
          if (
            hasFiniteNumber(day.actual_revenue) ||
            hasFiniteNumber(day.forecasted_revenue)
          ) {
            dailyTotal.tradingStoreCount += 1;
            if (!hasFiniteNumber(day.prior_year_actual_revenue)) {
              dailyTotal.priorYearMissingStoreNames.push(store.store_name);
            }
          }
          dailyTotal.forecasted_revenue += toNumber(day.forecasted_revenue);
          dailyTotal.forecasted_revenue_adjustment += toNumber(
            day.forecasted_revenue_adjustment
          );
          // Paired against last year so a store that was shut is dropped from
          // both sides, while a store whose date is still open in its own
          // timezone withholds the comparison as it did before.
          addComparisonValue(
            dailyTotal.actual_revenue_for_sales_comp,
            getSalesCompValue(
              day,
              "actual_revenue_for_sales_comp",
              day.actual_revenue
            ),
            day.prior_year_actual_revenue
          );
          addComparisonValue(
            dailyTotal.forecasted_revenue_for_sales_comp,
            getSalesCompValue(
              day,
              "forecasted_revenue_for_sales_comp",
              day.forecasted_revenue
            ),
            day.prior_year_actual_revenue
          );
          addComparisonValue(
            dailyTotal.forecasted_revenue_adjustment_for_sales_comp,
            getSalesCompValue(
              day,
              "forecasted_revenue_adjustment_for_sales_comp",
              day.forecasted_revenue_adjustment
            ),
            day.prior_year_actual_revenue
          );
          addNullableAggregateValue(
            dailyTotal.actual_revenue_for_labor_percent,
            getLaborMetricSales(
              day,
              "actual_revenue_for_labor_percent",
              day.actual_revenue,
              allStoresShowLaborPercent
            )
          );
          addNullableAggregateValue(
            dailyTotal.forecasted_revenue_for_labor_percent,
            getLaborMetricSales(
              day,
              "forecasted_revenue_for_labor_percent",
              day.forecasted_revenue,
              allStoresShowLaborPercent
            )
          );
          addNullableAggregateValue(
            dailyTotal.forecasted_revenue_adjustment_for_labor_percent,
            getLaborMetricSales(
              day,
              "forecasted_revenue_adjustment_for_labor_percent",
              day.forecasted_revenue_adjustment,
              allStoresShowLaborPercent
            )
          );
          addNullableAggregateValue(dailyTotal.labor_cost, day.labor_cost);
          addNullableAggregateValue(
            dailyTotal.scheduled_labor_cost,
            day.scheduled_labor_cost
          );
          addNullableAggregateValue(
            dailyTotal.forecasted_labor_cost,
            day.forecasted_labor_cost
          );
          addNullableAggregateValue(
            dailyTotal.earned_labor_cost,
            day.earned_labor_cost
          );
          dailyTotal.good_adjustment += getAdjustmentCount(day, "good_adjustment", "good_adjustments");
          dailyTotal.bad_adjustment += getAdjustmentCount(day, "bad_adjustment", "bad_adjustments");
          dailyTotal.scheduled_labor_hours += toNumber(day.scheduled_labor_hours);
          dailyTotal.forecasted_labor_hours += toNumber(day.forecasted_labor_hours);
          dailyTotal.actual_labor_hours += toNumber(day.actual_labor_hours);
          dailyTotal.earned_labor_hours += toNumber(day.earned_labor_hours);
          dailyTotal.published_shifts_count += toNumber(day.published_shifts_count);
          dailyTotal.auto_clock_out_count += toNumber(day.auto_clock_out_count);
          dailyTotal.missed_break_count += toNumber(day.missed_break_count);
          dailyTotal.missed_paid_break_count += toNumber(day.missed_paid_break_count);
          dailyTotal.missed_meal_break_count += toNumber(day.missed_meal_break_count);

          const plannedScore = toNumber(day.planned_score);
          const actualScore = toNumber(day.actual_score);
          if (day.planned_score_eligible ?? (plannedScore > 0)) {
            dailyTotal.plannedScoreTotal += plannedScore;
            dailyTotal.plannedScoreCount += 1;
          }
          if (day.actual_score_eligible ?? (actualScore > 0)) {
            dailyTotal.actualScoreTotal += actualScore;
            dailyTotal.actualScoreCount += 1;
          }
        });
      });

      const ref = overviewData[0].weekly_stats[weekIndex];
      if (!ref) return null;
      const dailyStats = Object.values(dailyTotalsByDate)
        .sort((a, b) => String(a.date).localeCompare(String(b.date)))
        .map((day) => ({
          date: day.date,
          actual_revenue: day.actual_revenue,
          // Sums the stores that have last year rather than blanking the whole
          // comparison over one store that does not. It therefore covers fewer
          // stores than the current-year rows, which the coverage note states.
          prior_year_actual_revenue: resolveNullableAggregate(
            day.prior_year_actual_revenue,
            false
          ),
          // A single date has no day count to compare, so the basis is which
          // stores were trading. Day counts are left null and the note phrases
          // itself in stores instead.
          sales_comparison_basis: {
            storeCount: day.tradingStoreCount,
            priorYearStoreCount:
              day.tradingStoreCount - day.priorYearMissingStoreNames.length,
            missingStoreNames: day.priorYearMissingStoreNames,
            priorYearDayCount: null,
            currentYearDayCount: null,
          },
          forecasted_revenue: day.forecasted_revenue,
          forecasted_revenue_adjustment: day.forecasted_revenue_adjustment,
          // Strict again. Closed stores were already dropped from both sides on
          // the way in, so what reaches here can only be a store that ought to
          // have a figure and does not, which still has to read as "Partial".
          actual_revenue_for_sales_comp: resolveNullableAggregate(
            day.actual_revenue_for_sales_comp,
            true
          ),
          forecasted_revenue_for_sales_comp: resolveNullableAggregate(
            day.forecasted_revenue_for_sales_comp,
            true
          ),
          forecasted_revenue_adjustment_for_sales_comp:
            resolveNullableAggregate(
              day.forecasted_revenue_adjustment_for_sales_comp,
              true
            ),
          actual_revenue_for_labor_percent: resolveNullableAggregate(
            day.actual_revenue_for_labor_percent,
            allStoresShowLaborPercent
          ),
          forecasted_revenue_for_labor_percent: resolveNullableAggregate(
            day.forecasted_revenue_for_labor_percent,
            allStoresShowLaborPercent
          ),
          forecasted_revenue_adjustment_for_labor_percent:
            resolveNullableAggregate(
              day.forecasted_revenue_adjustment_for_labor_percent,
              allStoresShowLaborPercent
            ),
          labor_cost: resolveNullableAggregate(
            day.labor_cost,
            allStoresShowLaborPercent
          ),
          scheduled_labor_cost: resolveNullableAggregate(
            day.scheduled_labor_cost,
            allStoresShowLaborPercent
          ),
          forecasted_labor_cost: resolveNullableAggregate(
            day.forecasted_labor_cost,
            allStoresShowLaborPercent
          ),
          earned_labor_cost: resolveNullableAggregate(
            day.earned_labor_cost,
            allStoresShowLaborPercent
          ),
          good_adjustment: day.good_adjustment,
          bad_adjustment: day.bad_adjustment,
          scheduled_labor_hours: day.scheduled_labor_hours,
          forecasted_labor_hours: day.forecasted_labor_hours,
          actual_labor_hours: day.actual_labor_hours,
          earned_labor_hours: day.earned_labor_hours,
          published_shifts_count: day.published_shifts_count,
          auto_clock_out_count: day.auto_clock_out_count,
          missed_break_count: day.missed_break_count,
          missed_paid_break_count: day.missed_paid_break_count,
          missed_meal_break_count: day.missed_meal_break_count,
          planned_score:
            day.plannedScoreCount > 0
              ? day.plannedScoreTotal / day.plannedScoreCount
              : 0,
          actual_score:
            day.actualScoreCount > 0
              ? day.actualScoreTotal / day.actualScoreCount
              : 0,
        }));

      return {
        scheduled_labor_hours: totalScheduled,
        forecasted_labor_hours: totalForecasted,
        actual_labor_hours: totalActual,
        earned_labor_hours: totalEarned,
        labor_cost: resolveNullableAggregate(
          totalLaborCost,
          allStoresShowLaborPercent
        ),
        scheduled_labor_cost: resolveNullableAggregate(
          totalScheduledLaborCost,
          allStoresShowLaborPercent
        ),
        forecasted_labor_cost: resolveNullableAggregate(
          totalForecastedLaborCost,
          allStoresShowLaborPercent
        ),
        earned_labor_cost: resolveNullableAggregate(
          totalEarnedLaborCost,
          allStoresShowLaborPercent
        ),
        forecasted_revenue: totalForecastedRevenue,
        forecasted_revenue_adjustment: totalForecastedRevenueAdjustment,
        actual_revenue: totalActualRevenue,
        prior_year_actual_revenue: resolveNullableAggregate(
          totalPriorYearActualRevenue,
          false
        ),
        sales_comparison_basis: {
          storeCount: tradingStoreCount,
          priorYearStoreCount:
            tradingStoreCount - priorYearMissingStoreNames.length,
          missingStoreNames: priorYearMissingStoreNames,
          priorYearDayCount,
          currentYearDayCount,
        },
        // Strict, on the same reasoning as the daily totals: stores that were
        // not trading are already out of both sides, so a null arriving here is
        // a store that should have had a figure.
        forecasted_revenue_for_sales_comp: resolveNullableAggregate(
          totalForecastedRevenueForSalesComp,
          true
        ),
        forecasted_revenue_adjustment_for_sales_comp:
          resolveNullableAggregate(
            totalForecastedRevenueAdjustmentForSalesComp,
            true
          ),
        actual_revenue_for_sales_comp: resolveNullableAggregate(
          totalActualRevenueForSalesComp,
          true
        ),
        forecasted_revenue_for_labor_percent: resolveNullableAggregate(
          totalForecastedRevenueForLaborPercent,
          allStoresShowLaborPercent
        ),
        forecasted_revenue_adjustment_for_labor_percent:
          resolveNullableAggregate(
            totalForecastedRevenueAdjustmentForLaborPercent,
            allStoresShowLaborPercent
          ),
        actual_revenue_for_labor_percent: resolveNullableAggregate(
          totalActualRevenueForLaborPercent,
          allStoresShowLaborPercent
        ),
        planned_score: plannedScoreCount > 0 ? totalPlannedScore / plannedScoreCount : 0,
        actual_score: actualScoreCount > 0 ? totalActualScore / actualScoreCount : 0,
        good_adjustments: totalGoodAdjustments,
        bad_adjustments: totalBadAdjustments,
        has_completed_week: contributingStoreCount > 0 && allStoresCompletedWeek,
        published_shifts_count: totalPublishedShifts,
        auto_clock_out_count: totalAutoClockOuts,
        missed_break_count: totalMissedBreaks,
        missed_paid_break_count: totalMissedPaidBreaks,
        missed_meal_break_count: totalMissedMealBreaks,
        week_start: ref?.week_start,
        start_date: ref?.start_date,
        end_date: ref?.end_date,
        daily_stats: dailyStats,
      };
    });
  }, [allStoresShowLaborPercent, overviewData]);

  // Function to get week label based on offset and index
  const getWeekLabel = (weekIndex) => {
    const relativePosition = weekOffset + weekIndex;

    if (relativePosition < -1) {
      return `${Math.abs(relativePosition)} Weeks Ago`;
    } else if (relativePosition === -1) {
      return "Last Week";
    } else if (relativePosition === 0) {
      return "Current Week";
    } else if (relativePosition === 1) {
      return "Next Week";
    } else {
      return `+${relativePosition} Weeks`;
    }
  };

  if (isOpeningSchedule) {
    return <OverviewScheduleLoadingState />;
  }

  // Handle empty data gracefully
  if (!overviewData || overviewData.length === 0) {
    if (isLoading) {
      return (
        <div>
          <div className="flex items-center mb-2 relative">
            <div className="w-[20%] flex items-center">
              <div className="relative flex-1">
                <SearchIcon
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  style={{ fontSize: "18px" }}
                />
                <input
                  className="w-full border border-gray-300 pl-9 pr-3 py-1.5 text-sm rounded-lg outline-none"
                  type="text"
                  placeholder="Search stores..."
                  disabled
                />
              </div>
              <button disabled className="ml-2 p-1.5 rounded-lg opacity-50 flex-shrink-0">
                <ChevronLeftIcon sx={{ fontSize: 24, color: '#475569' }} />
              </button>
            </div>
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className="flex-1 px-3">
                <p className="text-slate-600 text-xs font-medium mb-0.5">
                  {getWeekLabel(index)}
                </p>
                <div className="h-5 w-32 bg-slate-200 rounded animate-pulse"></div>
              </div>
            ))}
            <button disabled className="p-1.5 rounded-lg opacity-50 flex-shrink-0 absolute right-0">
              <ChevronRightIcon sx={{ fontSize: 24, color: '#475569' }} />
            </button>
          </div>
          <div className="space-y-2">
            {Array.from({ length: 5 }, (_, i) => (
              <div
                key={i}
                className="w-full bg-white border border-slate-200 rounded-lg flex shadow-sm overflow-hidden"
              >
                <div className="w-[20%] p-4 bg-gradient-to-br from-slate-50 to-slate-100 border-r border-slate-200">
                  <div className="animate-pulse flex items-center space-x-2">
                    <div className="w-6 h-6 bg-slate-200 rounded"></div>
                    <div className="h-5 w-24 bg-slate-200 rounded"></div>
                  </div>
                </div>
                {[0, 1, 2, 3].map((j) => (
                  <SkeletonWeekCell key={j} isPast={weekOffset + j < 0} />
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="mb-4">
            <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <StoreIcon className="w-8 h-8 text-slate-400" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-slate-700 mb-2">
            No Data Available
          </h3>
          <p className="text-slate-500 max-w-md">
            No overview data found for the selected parameters.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-2 relative">
        {/* Search bar */}
        <div className="w-[20%] flex items-center">
          <div className="relative flex-1">
            <SearchIcon
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              style={{ fontSize: "18px" }}
            />
            <input
              className={`w-full border border-gray-300 pl-9 pr-3 py-1.5 text-sm rounded-lg outline-none transition-all duration-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-100 ${
                filteredData.length === 0 && searchInput ? "border-red-300 focus:border-red-500 focus:ring-red-100" : ""
              }`}
              type="text"
              placeholder="Search stores..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
          {/* Left arrow button */}
          <button
            onClick={handlePreviousWeeks}
            disabled={isLoading}
            className="ml-2 p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            aria-label="Previous weeks"
          >
            <ChevronLeftIcon sx={{ fontSize: 24, color: '#475569' }} />
          </button>
        </div>

        {/* Week headers */}
        {[0, 1, 2, 3].map((index) => {
          const relativePosition = weekOffset + index;
          const isCurrentWeek = relativePosition === 0;
          const week = overviewData?.[0]?.weekly_stats?.[index];

          return (
            <div key={index} className="flex-1 px-3">
              <p
                className={`text-slate-600 text-xs mb-0.5 ${
                  isCurrentWeek ? "font-semibold text-slate-800" : "font-medium"
                }`}
              >
                {getWeekLabel(index)}
              </p>
              {week ? (
                <p
                  className={`text-slate-800 text-sm ${
                    isCurrentWeek ? "font-bold" : "font-medium"
                  }`}
                >
                  {formatDateReadable(week.start_date)} -{" "}
                  {formatDateReadable(week.end_date)}
                </p>
              ) : (
                <div className="h-5 w-32 bg-slate-200 rounded animate-pulse"></div>
              )}
            </div>
          );
        })}
        {/* Right arrow button */}
        <button
          onClick={handleNextWeeks}
          disabled={isLoading}
          className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 absolute right-0"
          aria-label="Next weeks"
        >
          <ChevronRightIcon sx={{ fontSize: 24, color: '#475569' }} />
        </button>
      </div>
      <div className="space-y-2">
        {aggregatedWeeklyStats && (
          <div className="w-full bg-white border border-slate-300 rounded-lg flex shadow-sm overflow-hidden">
            <div className="w-[20%] p-4 bg-gradient-to-br from-slate-100 to-slate-200 border-r border-slate-300">
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0">
                  <StoreIcon
                    sx={{
                      fontSize: '24px',
                      color: '#334155',
                    }}
                  />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 leading-tight">
                    All Stores
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">
                    {overviewData.length} stores
                  </p>
                </div>
              </div>
            </div>
            {aggregatedWeeklyStats.map((week, index) => {
              if (!week) return <SkeletonWeekCell key={index} isPast={weekOffset + index < 0} />;
              const scheduledHours = toNumber(week.scheduled_labor_hours);
              const forecastedHours = toNumber(week.forecasted_labor_hours);
              const forecastAdjustment = getForecastAdjustmentDetails(
                week.forecasted_revenue,
                week.forecasted_revenue_adjustment
              );
              const hasForecastAdjustment = forecastAdjustment.hasAdjustment;
              const forecastSales = hasForecastAdjustment
                ? toNumber(week.forecasted_revenue_adjustment)
                : toNumber(week.forecasted_revenue);
              const forecastSalesLabel = hasForecastAdjustment
                ? "Adjusted Forecast"
                : "Forecasted Sales";
              const actualHours = toNumber(week.actual_labor_hours);
              const earnedHours = toNumber(week.earned_labor_hours);

              return (
                <div
                  key={index}
                  role="button"
                  tabIndex={0}
                  className={`flex-1 p-3 border-l ${
                    week.has_completed_week
                      ? "bg-slate-50 hover:bg-slate-100"
                      : "bg-white hover:bg-slate-50"
                    } border-slate-200 cursor-pointer transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-500`}
                  onClick={() =>
                    openWeekDetail({
                      storeName: "All Stores",
                      week,
                      canOpenSchedule: false,
                      showLaborPercent: allStoresShowLaborPercent,
                    })
                  }
                  onKeyDown={(event) =>
                    handleWeekDetailKeyDown(event, {
                      storeName: "All Stores",
                      week,
                      canOpenSchedule: false,
                      showLaborPercent: allStoresShowLaborPercent,
                    })
                  }
                >
                  <WeeklySalesSummary week={week} />
                  <WeekLaborSummary
                    week={week}
                    scheduledHours={scheduledHours}
                    forecastedHours={forecastedHours}
                    actualHours={actualHours}
                    earnedHours={earnedHours}
                    forecastSales={forecastSales}
                    forecastSalesLabel={forecastSalesLabel}
                    showLaborPercent={allStoresShowLaborPercent}
                  />
                  </div>
              );
            })}
          </div>
        )}
        {filteredData.map((store, index) => {
          const storeShowLaborPercent = Boolean(store?.labor_percent_enabled);

          return (
            <div
              key={index}
              className="w-full bg-white border border-slate-200 rounded-lg flex shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
            >
              <div className="w-[20%] p-4 bg-gradient-to-br from-slate-50 to-slate-100 border-r border-slate-200">
                <div className="flex items-center space-x-2">
                  <div className="flex-shrink-0">
                    <StoreIcon
                      sx={{
                        fontSize: '24px',
                        color: '#475569',
                      }}
                    />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 leading-tight">
                    {store.store_name}
                  </h3>
                </div>
              </div>
              {store.weekly_stats?.map((week, index) => {
              if (!week) return <SkeletonWeekCell key={index} isPast={weekOffset + index < 0} />;
              const scheduledHours = toNumber(week.scheduled_labor_hours);
              const forecastedHours = toNumber(week.forecasted_labor_hours);
              const forecastAdjustment = getForecastAdjustmentDetails(
                week.forecasted_revenue,
                week.forecasted_revenue_adjustment
              );
              const hasForecastAdjustment = forecastAdjustment.hasAdjustment;
              const forecastSales = hasForecastAdjustment
                ? toNumber(week.forecasted_revenue_adjustment)
                : toNumber(week.forecasted_revenue);
              const forecastSalesLabel = hasForecastAdjustment
                ? "Adjusted Forecast"
                : "Forecasted Sales";
              const actualHours = toNumber(week.actual_labor_hours);
              const earnedHours = toNumber(week.earned_labor_hours);

              return (
                <div
                  key={index}
                  role="button"
                  tabIndex={0}
                  className={`flex-1 p-3 border-l ${
                    week.has_completed_week
                      ? "bg-slate-50 hover:bg-slate-100"
                      : "bg-white hover:bg-slate-50"
                    } border-slate-200 cursor-pointer transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-500 ${
                    isOpeningSchedule ? "pointer-events-none opacity-80" : ""
                    }`}
                  onClick={() =>
                    openWeekDetail({
                      storeId: store.store_id,
                      storeName: store.store_name,
                      week,
                      canOpenSchedule: canOpenStoreSchedule,
                      showLaborPercent: storeShowLaborPercent,
                    })
                  }
                  onKeyDown={(event) =>
                    handleWeekDetailKeyDown(event, {
                      storeId: store.store_id,
                      storeName: store.store_name,
                      week,
                      canOpenSchedule: canOpenStoreSchedule,
                      showLaborPercent: storeShowLaborPercent,
                    })
                  }
                >
                  <WeeklySalesSummary week={week} showUnpublished />
                  <WeekLaborSummary
                    week={week}
                    scheduledHours={scheduledHours}
                    forecastedHours={forecastedHours}
                    actualHours={actualHours}
                    earnedHours={earnedHours}
                    forecastSales={forecastSales}
                    forecastSalesLabel={forecastSalesLabel}
                    showLaborPercent={storeShowLaborPercent}
                  />
                  </div>
              );
            })}
            </div>
          );
        })}
      </div>
      <WeekDetailModal
        detail={selectedWeekDetail}
        onClose={() => setSelectedWeekDetail(null)}
        onOpenSchedule={handleOpenScheduleFromModal}
      />
    </div>
  );
}

export default FourWeekOverview;
